import { GraphInputHelpers, ONE_DAY_MS } from '@/fetching/graphql/GraphInputHelpers';
import { VisitDatetimeField, VisitOutcome } from '@/types/visit';
import {
    EmploymentType,
    EpisodeStatus,
    OrderType,
    Ordering,
    PatientFilter,
    SyncState,
    VisitFilter,
} from 'gql-types';
import moment from 'moment';

import { ContainerBulkAction } from '../ContainerVisitTable.types';
import {
    DEFAULT_CONTAINER_FILTER_WINDOW_DAYS,
    defaultEpisodeOfCareRowToLink,
    defaultEpisodeOrdersRowToLink,
    defaultEpisodeVisitsRowToLink,
    defaultVisitsRowToLink,
} from '../definitions';
import {
    EpisodeOfCareDashboardContainer,
    OrderDashboardContainer,
    PatientDashboardContainer,
    RootEntity,
    VisitDashboardContainer,
} from '../types';
import { PINNACLE_CONTAINER_KEYS } from './containerKeys/containerKeys';
import { HH_PATIENT_NO_SHOWS_CONTAINER } from './homeHealth';
import { IDashboardVariationConfig } from './types';

// Fulfillment windows ending on or before this date predate Pinnacle's go-live
// and should be excluded from the Overdue Visits and Underscheduled Patients
// queues. Applied with a strict `gt` against the visit's `latestEndDatetime`
// (Overdue Visits) and the order's `fulfillmentWindowEndDate` (Underscheduled).
const PINNACLE_FULFILLMENT_WINDOW_END_CUTOFF = '2026-05-24';
const TODAY = GraphInputHelpers.date(new Date());

// Pinnacle uses the current Medicare week (Sun -> Sat).
// Date is scalar so no times. So we land on 12am for Sunday and 11:59pm for Saturday bc of it.
// This is what we want.
const sundayOfThisWeek = (): Date => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay());
};
const _SUNDAY = sundayOfThisWeek();
const CURRENT_MEDICARE_WEEK_START = GraphInputHelpers.date(_SUNDAY);
const CURRENT_MEDICARE_WEEK_END = GraphInputHelpers.date(
    new Date(_SUNDAY.getFullYear(), _SUNDAY.getMonth(), _SUNDAY.getDate() + 6),
);

const EPISODE_ACTIVE_PRESENT_OR_FUTURE_FILTER = {
    status: { exact: EpisodeStatus.ACTIVE },
    endDate: { gte: TODAY },
};
const EPISODE_ACTIVE_PENDING_PRESENT_OR_FUTURE_FILTER = {
    status: { inList: [EpisodeStatus.ACTIVE, EpisodeStatus.PENDING] },
    endDate: { gte: TODAY },
};

// Pinnacle specific service IDs:
// https://docs.google.com/spreadsheets/d/14LjYsiJHynPc0TG_heeSec-F2JpPGPafoJSn8IB6K_M/edit?gid=1593865658#gid=1593865658

const OASIS_DISCHARGE_BASE_SERVICE_ID = '99562'; // OASIS-E2 Discharge
const OASIS_DISCHARGE_OT_SERVICE_ID = '99573'; // OASIS-E2 Discharge (OT)
const OASIS_DISCHARGE_PT_SERVICE_ID = '99568'; // OASIS-E2 Discharge (PT)
const OASIS_DISCHARGE_ST_SERVICE_ID = '99579'; // OASIS-E2 Discharge (ST)

const OASIS_DISCHARGE_NON_BILLABLE_BASE_SERVICE_ID = '99585'; // OASIS-E2 Discharge (Non-Billable)
const OASIS_DISCHARGE_NON_BILLABLE_OT_SERVICE_ID = '99556'; // OASIS-E2 Discharge (Non-Billable) - OT
const OASIS_DISCHARGE_NON_BILLABLE_PT_SERVICE_ID = '99555'; // OASIS-E2 Discharge (Non-Billable) - PT
const OASIS_DISCHARGE_NON_BILLABLE_ST_SERVICE_ID = '99557'; // OASIS-E2 Discharge (Non-Billable) - ST

const OASIS_DEATH_BASE_SERVICE_ID = '99563'; // OASIS-E2 Death
const OASIS_DEATH_OT_SERVICE_ID = '99574'; // OASIS-E2 Death (OT)
const OASIS_DEATH_PT_SERVICE_ID = '99569'; // OASIS-E2 Death (PT)
const OASIS_DEATH_ST_SERVICE_ID = '99580'; // OASIS-E2 Death (ST)

const OASIS_DISCHARGE_ONLY_SERVICE_IDS = [
    OASIS_DISCHARGE_BASE_SERVICE_ID,
    OASIS_DISCHARGE_OT_SERVICE_ID,
    OASIS_DISCHARGE_PT_SERVICE_ID,
    OASIS_DISCHARGE_ST_SERVICE_ID,
    OASIS_DISCHARGE_NON_BILLABLE_BASE_SERVICE_ID,
    OASIS_DISCHARGE_NON_BILLABLE_OT_SERVICE_ID,
    OASIS_DISCHARGE_NON_BILLABLE_PT_SERVICE_ID,
    OASIS_DISCHARGE_NON_BILLABLE_ST_SERVICE_ID,
];

const OASIS_DISCHARGE_SERVICE_IDS = [
    ...OASIS_DISCHARGE_ONLY_SERVICE_IDS,
    OASIS_DEATH_BASE_SERVICE_ID,
    OASIS_DEATH_OT_SERVICE_ID,
    OASIS_DEATH_PT_SERVICE_ID,
    OASIS_DEATH_ST_SERVICE_ID,
];

const OASIS_E2_FOLLOWUP_BASE_SERVICE_ID = '99561'; // OASIS-E2 Follow-up (SN)
const OASIS_E2_FOLLOWUP_OT_SERVICE_ID = '99572'; // OASIS-E2 Follow-up (OT)
const OASIS_E2_FOLLOWUP_PT_SERVICE_ID = '99567'; // OASIS-E2 Follow-up (PT)
const OASIS_E2_FOLLOWUP_ST_SERVICE_ID = '99578'; // OASIS-E2 Follow-up (ST)

// OASIS-E2 Follow-up service IDs (anchor for SCIC. One per discipline).
const OASIS_E2_FOLLOWUP_SERVICE_IDS = [
    OASIS_E2_FOLLOWUP_BASE_SERVICE_ID,
    OASIS_E2_FOLLOWUP_OT_SERVICE_ID,
    OASIS_E2_FOLLOWUP_PT_SERVICE_ID,
    OASIS_E2_FOLLOWUP_ST_SERVICE_ID,
];

// Non-visit tasks that are tracked in the Patients Missing Required Tasks queue.
const NOMNC_SERVICE_IDS = ['2456', '60332']; // Notice of Medicare Non-Coverage, NOMNC
const CMS_485_SERVICE_ID = '248'; // CMS 485
const SCIC_ORDER_SERVICE_ID = '60517'; // SCIC
const POST_HOSPITAL_ORDER_SERVICE_ID = '256'; // Post Hospital Order

const DISCHARGE_SUMMARY_RN_SERVICE_ID = '81740'; // Discharge Summary (RN)
const DISCHARGE_SUMMARY_OT_SERVICE_ID = '25372'; // Discharge Summary (OT)
const DISCHARGE_SUMMARY_PT_SERVICE_ID = '25375'; // Discharge Summary (PT)
const DISCHARGE_SUMMARY_ST_SERVICE_ID = '25376'; // Discharge Summary (ST)
const DISCHARGE_SUMMARY_MSW_SERVICE_ID = '25424'; // Discharge Summary (MSW)

const DISCHARGE_SUMMARY_SERVICE_IDS = [
    DISCHARGE_SUMMARY_RN_SERVICE_ID,
    DISCHARGE_SUMMARY_OT_SERVICE_ID,
    DISCHARGE_SUMMARY_PT_SERVICE_ID,
    DISCHARGE_SUMMARY_ST_SERVICE_ID,
    DISCHARGE_SUMMARY_MSW_SERVICE_ID,
];

const NON_BILLABLE_SUPERVISORY_VISIT_RN_SERVICE_ID = '98374'; // Non-Billable Supervisory Visit (RN)
const NON_BILLABLE_SUPERVISORY_VISIT_OT_SERVICE_ID = '98381'; // Non-Billable Supervisory Visit (OT)
const NON_BILLABLE_SUPERVISORY_VISIT_PT_SERVICE_ID = '98385'; // Non-Billable Supervisory Visit (PT)

const NON_BILLABLE_SUPERVISORY_VISIT_SERVICE_IDS = [
    NON_BILLABLE_SUPERVISORY_VISIT_RN_SERVICE_ID,
    NON_BILLABLE_SUPERVISORY_VISIT_OT_SERVICE_ID,
    NON_BILLABLE_SUPERVISORY_VISIT_PT_SERVICE_ID,
];

const OT_DISCIPLINE_DISCHARGE_SERVICE_IDS = [
    '3681', // OT Discharge Eval w/ Discharge Summary
    DISCHARGE_SUMMARY_OT_SERVICE_ID,
];

const PT_DISCIPLINE_DISCHARGE_SERVICE_IDS = [
    '35055', // PT Discharge w/Discharge Summary
    DISCHARGE_SUMMARY_PT_SERVICE_ID,
];

const SN_DISCIPLINE_DISCHARGE_SERVICE_IDS = [
    '29547', // SNV W/ Discharge Summary
    DISCHARGE_SUMMARY_RN_SERVICE_ID,
];

const ST_DISCIPLINE_DISCHARGE_SERVICE_IDS = [DISCHARGE_SUMMARY_ST_SERVICE_ID];

const MSW_DISCIPLINE_DISCHARGE_SERVICE_IDS = [DISCHARGE_SUMMARY_MSW_SERVICE_ID];

const RECERT_BASE_SERVICE_ID = '99581'; // OASIS-E2 Recertification
const RECERT_OT_SERVICE_ID = '99583'; // OASIS-E2 Recertification (OT)
const RECERT_PT_SERVICE_ID = '99582'; // OASIS-E2 Recertification (PT)
const RECERT_ST_SERVICE_ID = '99584'; // OASIS-E2 Recertification (ST)

const OASIS_RECERT_SERVICE_IDS = [
    RECERT_BASE_SERVICE_ID,
    RECERT_OT_SERVICE_ID,
    RECERT_PT_SERVICE_ID,
    RECERT_ST_SERVICE_ID,
];

const SOC_BASE_SERVICE_ID = '99558'; // OASIS-E2 Start of Care
const SOC_OT_SERVICE_ID = '99586'; // OASIS-E2 Start of Care (OT)
const SOC_PT_SERVICE_ID = '99564'; // OASIS-E2 Start of Care (PT)
const SOC_ST_SERVICE_ID = '99575'; // OASIS-E2 Start of Care (ST)

const SOC_SERVICE_IDS = [
    SOC_BASE_SERVICE_ID,
    SOC_OT_SERVICE_ID,
    SOC_PT_SERVICE_ID,
    SOC_ST_SERVICE_ID,
];

const ROC_BASE_SERVICE_ID = '99559'; // OASIS-E2 Resumption of Care
const ROC_OT_SERVICE_ID = '99570'; // OASIS-E2 Resumption of Care (OT)
const ROC_PT_SERVICE_ID = '99565'; // OASIS-E2 Resumption of Care (PT)
const ROC_ST_SERVICE_ID = '99576'; // OASIS-E2 Resumption of Care (ST)

const ROC_SERVICE_IDS = [
    ROC_BASE_SERVICE_ID,
    ROC_OT_SERVICE_ID,
    ROC_PT_SERVICE_ID,
    ROC_ST_SERVICE_ID,
];

const PT_EVAL_SERVICE_IDS = [
    '35304', // PT Evaluation
    '98065', // PT LSVT Eval
    '98070', // PT Lymphedema Eval
    '98074', // PT Vestibular RT Eval
];
const OT_EVAL_SERVICE_IDS = [
    '73472', // OT Eval Lymphedema
    '28387', // OT Evaluation
];
const ST_EVAL_SERVICE_IDS = [
    '31742', // ST Eval
];

const EVAL_SERVICE_IDS = [
    ...PT_EVAL_SERVICE_IDS,
    ...OT_EVAL_SERVICE_IDS,
    ...ST_EVAL_SERVICE_IDS,
    '4506', // MSW Evaluation
    OASIS_E2_FOLLOWUP_BASE_SERVICE_ID,
];

// Service codes that satisfy the re-eval requirement for the Pending Therapy
// Re-Evaluation queue: re-evals, SOC/ROC/Recert/Eval, plus discharge and
// death visits which also satisfy the requirement (ENG-8666).
const REASSESSMENT_SERVICE_IDS = [
    '28388', // OT Re-Evaluation
    '28300', // OT Re-Evaluation w/Supervisory Visit
    '98113', // OT Re-Eval Lymphedema
    '83723', // PT Re-Evaluation
    '35172', // PT Re-evaluation w/Supervisory Visit
    '98067', // PT Lymphedema Re-Eval
    '31744', // ST Re-Evaluation
    '99562', // OASIS-E2 Discharge
    '99585', // OASIS-E2 Discharge (Non-Billable)
    '99563', // OASIS-E2 Death
    '99568', // OASIS-E2 Discharge (PT)
    '99555', // OASIS-E2 Discharge (Non-Billable) - PT
    '99569', // OASIS-E2 Death (PT)
    '99573', // OASIS-E2 Discharge (OT)
    '99556', // OASIS-E2 Discharge (Non-Billable) - OT
    '99574', // OASIS-E2 Death (OT)
    ...SOC_SERVICE_IDS,
    ...ROC_SERVICE_IDS,
    ...OASIS_RECERT_SERVICE_IDS,
    ...EVAL_SERVICE_IDS,
];

const TRANSFER_SERVICE_IDS = [
    '99560', // OASIS-E2 Transfer
    '99571', // OASIS-E2 Transfer (OT)
    '99566', // OASIS-E2 Transfer (PT)
    '99577', // OASIS-E2 Transfer (ST)
    '98375', // Transfer Summary (RN)
    '98382', // Transfer Summary (OT)
    '98386', // Transfer Summary (PT)
    '25481', // Transfer Summary (ST)
];

// Service codes that resolve an on-hold (transferred) episode and therefore
// should NOT be flagged as "pending visits to cancel" on the Transferred
// Patients WQ. Per the parent PRD spec; intentionally excludes ST variants.
const TRANSFER_RESOLUTION_SERVICE_IDS = [
    ROC_OT_SERVICE_ID,
    ROC_PT_SERVICE_ID,
    OASIS_DISCHARGE_BASE_SERVICE_ID,
    OASIS_DISCHARGE_OT_SERVICE_ID,
    OASIS_DISCHARGE_PT_SERVICE_ID,
    OASIS_DISCHARGE_NON_BILLABLE_BASE_SERVICE_ID,
    OASIS_DISCHARGE_NON_BILLABLE_OT_SERVICE_ID,
    OASIS_DISCHARGE_NON_BILLABLE_PT_SERVICE_ID,
    OASIS_DEATH_BASE_SERVICE_ID,
    OASIS_DEATH_OT_SERVICE_ID,
    OASIS_DEATH_PT_SERVICE_ID,
];

// PTA visits per PRD: anchor for the PTA branch of the Pending Therapy Supervisory queue.
const PTA_VISIT_SERVICE_IDS = [
    '98077', // PTA LSVT Visit
    '35846', // PTA Lymphedema Visit
    '62949', // PTA Maintenance Visit
    '64960', // PTA Telehealth
    '98081', // PTA Vestibular RT Visit
    '6507', // PTA Visit
];

// OTA / COTA visits per PRD: anchor for the OTA branch. OTA visits are covered by OT supervisory visits.
const OTA_VISIT_SERVICE_IDS = [
    '98116', // OTA Lymphedema Visit
    '86793', // OTA Maintenance Visit
    '98118', // OTA Telehealth
    '87101', // OTA Visit
];

const PT_THERAPY_SUPERVISORY_SERVICE_IDS = [
    NON_BILLABLE_SUPERVISORY_VISIT_PT_SERVICE_ID,
    RECERT_PT_SERVICE_ID,
    ROC_PT_SERVICE_ID,
    SOC_PT_SERVICE_ID,
    ...PT_EVAL_SERVICE_IDS,
    '35172', // PT Re-evaluation w/Supervisory Visit
    '98387', // PT Visit w/Supervisory Visit
    OASIS_DISCHARGE_BASE_SERVICE_ID,
    OASIS_DISCHARGE_PT_SERVICE_ID,
    OASIS_DISCHARGE_NON_BILLABLE_BASE_SERVICE_ID,
    OASIS_DISCHARGE_NON_BILLABLE_PT_SERVICE_ID,
    OASIS_DEATH_BASE_SERVICE_ID,
    OASIS_DEATH_PT_SERVICE_ID,
    ...PT_DISCIPLINE_DISCHARGE_SERVICE_IDS,
];

// OT supervisory codes that cover an OTA visit, per PRD-619. Same shape as PT.
const OT_THERAPY_SUPERVISORY_SERVICE_IDS = [
    NON_BILLABLE_SUPERVISORY_VISIT_OT_SERVICE_ID,
    RECERT_OT_SERVICE_ID,
    ROC_OT_SERVICE_ID,
    SOC_OT_SERVICE_ID,
    ...OT_EVAL_SERVICE_IDS,
    '28300', // OT Re-Evaluation w/Supervisory Visit
    '3680', // OT Visit w/ Supervisory Visit
    OASIS_DISCHARGE_BASE_SERVICE_ID,
    OASIS_DISCHARGE_OT_SERVICE_ID,
    OASIS_DISCHARGE_NON_BILLABLE_BASE_SERVICE_ID,
    OASIS_DISCHARGE_NON_BILLABLE_OT_SERVICE_ID,
    OASIS_DEATH_BASE_SERVICE_ID,
    OASIS_DEATH_OT_SERVICE_ID,
    ...OT_DISCIPLINE_DISCHARGE_SERVICE_IDS,
];

// HHA supervisory codes per PRD-618.
const HHA_SUPERVISORY_SERVICE_IDS = [
    '2941', // Aide Supervisory Visit
    '98376', // RN Visit w/Supervisory Visit
    '35172', // PT Re-evaluation w/Supervisory Visit
    '98387', // PT Visit w/Supervisory Visit
    '28300', // OT Re-Evaluation w/Supervisory Visit
    '3680', // OT Visit w/ Supervisory Visit
    SOC_BASE_SERVICE_ID,
    SOC_OT_SERVICE_ID,
    SOC_PT_SERVICE_ID,
    ...NON_BILLABLE_SUPERVISORY_VISIT_SERVICE_IDS,
    ...OASIS_RECERT_SERVICE_IDS,
    ...ROC_SERVICE_IDS,
    ...OASIS_DISCHARGE_SERVICE_IDS,
];

// Regular HHA visits — anchor for the Pending HHA Supervisory queue.
// TODO (ask David): confirm this is the right anchor list — PRD only says
// "future HHA visit" without enumerating.
const HHA_VISIT_SERVICE_IDS = [
    '29565', // HHA Visit
];

// LPN supervisory codes per PRD: includes RN supervisory codes AND OASIS-E2
// Recert/ROC (which count as supervisory for an LPN per the PRD spec), plus
// discharge and death visits which also satisfy the requirement (ENG-8665).
const LPN_SUPERVISORY_SERVICE_IDS = [
    '99558', // OASIS-E2 Start of Care
    OASIS_E2_FOLLOWUP_BASE_SERVICE_ID,
    '98374', // Non-Billable Supervisory Visit (RN)
    '98376', // RN Visit w/Supervisory Visit
    '99581', // OASIS-E2 Recertification
    '99559', // OASIS-E2 Resumption of Care
    '29547', // SNV W/ Discharge Summary
    '81740', // Discharge Summary (RN)
    '99562', // OASIS-E2 Discharge
    '99585', // OASIS-E2 Discharge (Non-Billable)
    '99563', // OASIS-E2 Death
    '99568', // OASIS-E2 Discharge (PT)
    '99555', // OASIS-E2 Discharge (Non-Billable) - PT
    '99569', // OASIS-E2 Death (PT)
    '99573', // OASIS-E2 Discharge (OT)
    '99556', // OASIS-E2 Discharge (Non-Billable) - OT
    '99574', // OASIS-E2 Death (OT)
];

// Regular LPN visits — anchor for the Pending LPN Supervisory queue.
// TODO (ask David): confirm this is the right anchor list — PRD only says
// "future LPN visit" without enumerating.
const LPN_VISIT_SERVICE_IDS = [
    '98099', // LPN Skilled Visit
    '98094', // High-Tech LPN Visit
    '77370', // LPN Foley Change
    '98098', // LPN Phone
    '98100', // LPN Telehealth
    '98101', // LPN Train/Educate
];

// Non-visit tasks related to SOC -- Medication Profile, Consent, and Disaster Plan
const SOC_RELATED_NON_VISIT_TASKS = [
    '398', // Consent
    '100131', // Patient Consent
    '98497', // Disaster Plan
    '417', // Medication Profile
];

// Wellsky Non-visit tasks to ignore
const NON_VISIT_TASKS_SERVICE_IDS_TO_IGNORE_POST_DISCHARGE = [
    ...NOMNC_SERVICE_IDS, // Notice of Medicare Non-Coverage, NOMNC
    '99675', // 60-Day Conference
    '248', // CMS 485
    '96785', // Delay in SOC Communication
    '60517', // SCIC
    '256', // Post Hospital Order
    '6848', // Patient Communication
    '100740', // Client Alert
    '328', // Other Document
    '98294', // Welcome Call Communication Note
];

// Service codes that should NOT count as "pending visits needing scheduler
// action" on the post-discharge / post-transfer queues (Discipline Discharge,
// Agency Discharged, Transferred, Upcoming Discharge).
const POST_DISCHARGE_EXCLUDED_SERVICE_IDS = [
    ...SOC_RELATED_NON_VISIT_TASKS,
    ...NON_VISIT_TASKS_SERVICE_IDS_TO_IGNORE_POST_DISCHARGE,
    ...DISCHARGE_SUMMARY_SERVICE_IDS,
    ...NON_BILLABLE_SUPERVISORY_VISIT_SERVICE_IDS,
];

// OASIS Death assessments (E1 + E2). Real scheduled visits, not tasks.
const OASIS_DEATH_SERVICE_IDS = [
    // E1 (RN/OT/PT/ST)
    '92482',
    '92493',
    '92488',
    '92499',
    // E2 (RN/OT/PT/ST)
    OASIS_DEATH_BASE_SERVICE_ID,
    OASIS_DEATH_OT_SERVICE_ID,
    OASIS_DEATH_PT_SERVICE_ID,
    OASIS_DEATH_ST_SERVICE_ID,
];

// OASIS Transfer assessments (E1 + E2). Real scheduled visits, not tasks.
const OASIS_TRANSFER_SERVICE_IDS = [
    // E1 (RN/OT/PT/ST)
    '92479',
    '92490',
    '92485',
    '92496',
    // E2 (RN/OT/PT/ST)
    '99560',
    '99571',
    '99566',
    '99577',
];

// Pure paperwork/admin non-visit tasks that are never a real scheduled visit.
const NON_VISIT_TASK_ONLY_SERVICE_IDS = [
    // Discharge Summaries (RN/OT/PT/ST/MSW)
    ...DISCHARGE_SUMMARY_SERVICE_IDS,
    // SOC documents: Consent, Disaster Plan, Medication Profile
    ...SOC_RELATED_NON_VISIT_TASKS,
    // Compliance / care-management non-visit tasks:
    // NOMNC, 60-Day Conference, CMS 485, Delay in SOC Communication, SCIC, Post Hospital Order
    ...NON_VISIT_TASKS_SERVICE_IDS_TO_IGNORE_POST_DISCHARGE,
    '390', // Aide Care Plan
    // Non-Billable Supervisory Visits (RN/OT/PT)
    ...NON_BILLABLE_SUPERVISORY_VISIT_SERVICE_IDS,
    // Transfer Summaries (RN/OT/PT/ST). Documents, not OASIS Transfer visits.
    '98375',
    '98382',
    '98386',
    '25481',
    // OASIS-E1 Discharge (Non-Billable) (RN/OT/PT/ST)
    '92504',
    '92475',
    '92474',
    '92476',
    // OASIS-E2 Discharge (Non-Billable) (RN/OT/PT/ST)
    OASIS_DISCHARGE_NON_BILLABLE_BASE_SERVICE_ID,
    OASIS_DISCHARGE_NON_BILLABLE_OT_SERVICE_ID,
    OASIS_DISCHARGE_NON_BILLABLE_PT_SERVICE_ID,
    OASIS_DISCHARGE_NON_BILLABLE_ST_SERVICE_ID,
];

// Full non-visit task list for the homepage "Today's Visits" exclusion:
// admin tasks plus OASIS death and transfer assessments.
// Order does not matter here; the set matches the prior list.
const NON_VISIT_TASK_SERVICE_IDS = [
    ...NON_VISIT_TASK_ONLY_SERVICE_IDS,
    ...OASIS_DEATH_SERVICE_IDS,
    ...OASIS_TRANSFER_SERVICE_IDS,
];

const AUTH_RELATED_SYNC_ERROR_TYPES = [
    'PINNACLE_VISIT_NOT_AUTHORIZED_ERROR',
    'PINNACLE_VISIT_WELLSKY_REJECTED',
    'PINNACLE_VISIT_BLOCKED_BY_HIGHER_AUTH_PRIORITY_VISIT',
];

// SOC visits that have not yet been processed
// 1. A visit has a service code in the list below
// 2. That visit is missing one of the following clinician or date
// 3. The visit's episode is Active or Pending with an end date today or later (FE-678)
// sorting: Sorted by days left in the fulfillment window, lowest first
const SOC_CONTAINER: VisitDashboardContainer = {
    key: PINNACLE_CONTAINER_KEYS.soc,
    label: 'SOC (Start of Care)',
    defaultEnabled: true,
    hoverText: 'SOCs that need to be scheduled.',
    snoozable: true,
    tableConfig: {
        entity: RootEntity.VISIT,
        highlightLinkedVisit: true,
        filters: {
            services: { externalIds: { value: { inList: SOC_SERVICE_IDS } } },
            needsAssignment: { exact: true },
            episode: EPISODE_ACTIVE_PENDING_PRESENT_OR_FUTURE_FILTER,
        },
        rowToLink: defaultVisitsRowToLink,
        emptyTableText: 'No SOC visits to schedule.',
        ordering: { effectiveSortDate: Ordering.ASC },
    },
};

// ROC visits that have not yet been processed
// 1. A visit has a service code in the list below
// 2. That visit is missing one of the following clinician or date
// 3. The visit's episode is Active or On Hold with an end date today or later (FE-678)
// sorting: By number of days left in fulfillment window, lowest first
const ROC_CONTAINER: VisitDashboardContainer = {
    key: PINNACLE_CONTAINER_KEYS.roc,
    label: 'ROC (Resumption of Care)',
    defaultEnabled: true,
    hoverText: 'ROCs that need to be scheduled.',
    snoozable: true,
    tableConfig: {
        entity: RootEntity.VISIT,
        highlightLinkedVisit: true,
        filters: {
            services: { externalIds: { value: { inList: ROC_SERVICE_IDS } } },
            needsAssignment: { exact: true },
            episode: {
                status: { inList: [EpisodeStatus.ACTIVE, EpisodeStatus.ON_HOLD] },
                endDate: { gte: TODAY },
            },
        },
        rowToLink: defaultVisitsRowToLink,
        emptyTableText: 'No ROC visits to schedule.',
        ordering: { effectiveSortDate: Ordering.ASC },
    },
};

// Eval visits that have not yet been processed
// 1. A visit has a service code in the list below
// 2. That visit is missing one of the following clinician or date
// 3. The visit's episode is Active or Pending with an end date today or later (FE-678)
// sorting: Sorted by number of days left in the Eval window, lowest first
const EVAL_CONTAINER: VisitDashboardContainer = {
    key: PINNACLE_CONTAINER_KEYS.eval,
    label: 'Eval',
    defaultEnabled: true,
    hoverText: 'Evals that need to be scheduled.',
    snoozable: true,
    tableConfig: {
        entity: RootEntity.VISIT,
        highlightLinkedVisit: true,
        filters: {
            services: { externalIds: { value: { inList: EVAL_SERVICE_IDS } } },
            needsAssignment: { exact: true },
            episode: {
                status: {
                    inList: [EpisodeStatus.ACTIVE, EpisodeStatus.PENDING, EpisodeStatus.ON_HOLD],
                },
                endDate: { gte: TODAY },
            },
        },
        rowToLink: defaultVisitsRowToLink,
        emptyTableText: 'No Eval visits to schedule.',
        ordering: { effectiveSortDate: Ordering.ASC },
    },
};

// SOCs/ROCs/Eval visits that don't have a linked Order
// 1. A patient has a SOC/ROC/Eval visit with outcome = Completed that
//    does not have a linked order
// 2. The patient is active, and the visit's episode is Active and currently
//    includes today (FE-678)
// sorting: Sorted by visits with the soonest SOC, ROC, or Eval first
const COMPLETED_VISITS_MISSING_AN_ORDER_CONTAINER: VisitDashboardContainer = {
    key: PINNACLE_CONTAINER_KEYS.completedVisitsMissingAnOrder,
    label: 'Completed Visits Missing an Order',
    defaultEnabled: false,
    hoverText: "Completed SOCs/ROCs/Evals that aren't yet associated to an order.",
    heavyQuery: true,
    snoozable: true,
    tableConfig: {
        entity: RootEntity.VISIT,
        highlightLinkedVisit: true,
        filters: {
            outcome: { exact: VisitOutcome.COMPLETED },
            hasOrder: { exact: false },
            patient: { isActive: { exact: true } },
            episode: EPISODE_ACTIVE_PRESENT_OR_FUTURE_FILTER,
            services: {
                externalIds: {
                    value: {
                        inList: [...SOC_SERVICE_IDS, ...ROC_SERVICE_IDS, ...EVAL_SERVICE_IDS],
                    },
                },
            },
        },
        rowToLink: defaultVisitsRowToLink,
        emptyTableText: 'No completed SOC/ROC/Eval visits unlinked to orders.',
        ordering: { effectiveSortDate: Ordering.ASC },
    },
};

// Evals that are scheduled or completed prior to the patient's SOC
// 1. A patient has a SOC visit with an outcome = None or Completed
// 2. The patient has an Eval visit with outcome = None or Completed
//    with a date and time prior to that SOC
// 3. The Eval's episode is Active or Pending with an end date today or later (FE-678)
// sorting: Visits are sorted by date with the soonest EVAL visits at the top
// TODO (ask Jessica) (1) include Completed SOCs too? HH_EVALS_BEFORE_SOCS_CONTAINER vs PRD
const VISITS_SCHEDULED_BEFORE_SOC_CONTAINER: VisitDashboardContainer = {
    key: PINNACLE_CONTAINER_KEYS.visitsScheduledBeforeSoc,
    label: 'Visits Scheduled Before SOC',
    defaultEnabled: false,
    heavyQuery: true,
    hoverText: "Visits that are scheduled or completed prior to the patient's SOC.",
    snoozable: true,
    tableConfig: {
        entity: RootEntity.VISIT,
        highlightLinkedVisit: true,
        filters: {
            services: { externalIds: { value: { inList: EVAL_SERVICE_IDS } } },
            outcome: { inList: [VisitOutcome.NONE, VisitOutcome.COMPLETED] },
            episode: EPISODE_ACTIVE_PENDING_PRESENT_OR_FUTURE_FILTER,
            relatedVisitCondition: {
                exists: true,
                samePatient: true,
                filter: {
                    services: { externalIds: { value: { inList: SOC_SERVICE_IDS } } },
                    outcome: { inList: [VisitOutcome.NONE, VisitOutcome.COMPLETED] },
                    effectiveDate: { gte: GraphInputHelpers.date(new Date()) },
                },
                timeConstraints: [
                    {
                        field: VisitDatetimeField.EFFECTIVE_DATE,
                        relativeTo: VisitDatetimeField.EFFECTIVE_DATE,
                        gtOffsetMinutes: 0,
                    },
                ],
            },
        },
        rowToLink: defaultVisitsRowToLink,
        emptyTableText: 'No Evals scheduled before SOCs.',
        ordering: { effectiveSortDate: Ordering.ASC },
    },
};

// Visits that need to be scheduled and are nearing the end of their fulfillment window
// 1. A visit is missing one of the following service, clinician, or date
// 2. The visit's fulfillment window ends 7 days from today or earlier (no lower bound)
// 3. The visit's episode is Active and currently includes today (FE-678)
// sorting: Sorted by time until the fulfillment window end date, closest first
const URGENT_VISITS_NEEDING_SCHEDULING_CONTAINER: VisitDashboardContainer = {
    key: PINNACLE_CONTAINER_KEYS.urgentVisitsNeedingScheduling,
    label: 'Urgent Visits Needing Scheduling',
    defaultEnabled: true,
    hoverText: 'Visits that need to be scheduled within the next 7 days.',
    heavyQuery: true,
    snoozable: true,
    tableConfig: {
        entity: RootEntity.VISIT,
        highlightLinkedVisit: true,
        filters: {
            patient: {
                isActive: { exact: true },
            },
            episode: EPISODE_ACTIVE_PRESENT_OR_FUTURE_FILTER,
            needsAssignment: { exact: true },
            latestEndDatetime: {
                gte: GraphInputHelpers.date(new Date()),
                lte: GraphInputHelpers.date(new Date(Date.now() + 7 * ONE_DAY_MS)),
            },
        },
        rowToLink: defaultVisitsRowToLink,
        emptyTableText: 'No visits with expiring fulfillment windows.',
        ordering: { effectiveSortDate: Ordering.ASC },
    },
};

// Patients nearing their episode end with no recert or discharge visit scheduled
// 1. The episode is in Active status and ends between 0 and 10 days from today.
// 2. There is no visit on that episode with a service code mapped to Recert or Discharge that
//    has been scheduled (not missing service, clinician, or date) or completed.
// 3. Or, there is no visit on that episode with a service code mapped to ROC
//    that has been scheduled or completed within the last 5 days of the episode.
// Sorting: by days until the episode end date, lowest number first.
const UPCOMING_END_OF_EPISODE_CONTAINER: EpisodeOfCareDashboardContainer = {
    key: PINNACLE_CONTAINER_KEYS.upcomingEndOfEpisode,
    label: 'Upcoming End of Episode',
    defaultEnabled: false,
    heavyQuery: true,
    hoverText:
        'Patients nearing the end of their episode with no recert, discharge, or recent ROC visit scheduled.',
    snoozable: true,
    tableConfig: {
        entity: RootEntity.EPISODE_OF_CARE,
        filters: {
            status: { exact: EpisodeStatus.ACTIVE },
            patient: { isActive: { exact: true } },
            endDate: {
                gte: GraphInputHelpers.date(new Date()),
                lte: GraphInputHelpers.date(new Date(Date.now() + 10 * ONE_DAY_MS)),
            },
            NOT: {
                hasVisits: {
                    services: {
                        externalIds: {
                            value: {
                                inList: [
                                    ...OASIS_DISCHARGE_SERVICE_IDS,
                                    ...OASIS_RECERT_SERVICE_IDS,
                                ],
                            },
                        },
                    },
                    outcome: { inList: [VisitOutcome.NONE, VisitOutcome.COMPLETED] },
                },
            },
            AND: {
                NOT: {
                    hasVisits: {
                        services: {
                            externalIds: { value: { inList: ROC_SERVICE_IDS } },
                        },
                        outcome: { inList: [VisitOutcome.NONE, VisitOutcome.COMPLETED] },
                        effectiveDate: {
                            gte: GraphInputHelpers.date(new Date(Date.now() - 5 * ONE_DAY_MS)),
                            lte: GraphInputHelpers.date(new Date(Date.now() + 10 * ONE_DAY_MS)),
                        },
                    },
                },
            },
        },
        rowToLink: defaultEpisodeOfCareRowToLink,
        emptyTableText: 'No episodes pending end of care.',
        ordering: { patient: { fullName: Ordering.ASC } },
    },
};

// Patients with visits that still need a service, clinician, or date
// 1. A patient has at least one visit missing one of the following
//    service, clinician, or date
// 2. The episode is Active and currently includes today (FE-678)
// sorting: Sorted by fulfillment window end date, the soonest window end date first
// TODO (ask Jessica) (sorting) no min visit fulfillment window.
const PATIENTS_NEEDING_SCHEDULING_CONTAINER: EpisodeOfCareDashboardContainer = {
    key: PINNACLE_CONTAINER_KEYS.patientsNeedingScheduling,
    label: 'Patients Needing Scheduling',
    defaultEnabled: true,
    hoverText: 'Patients with visits that need to be scheduled.',
    snoozable: true,
    tableConfig: {
        entity: RootEntity.EPISODE_OF_CARE,
        filters: {
            ...EPISODE_ACTIVE_PRESENT_OR_FUTURE_FILTER,
            hasVisits: {
                needsAssignment: { exact: true },
            },
        },
        rowToLink: defaultEpisodeOfCareRowToLink,
        emptyTableText: 'No patients to schedule.',
        // TODO
        // sorting: "fulfillment window end date, soonest first"
        // this is min(visit.fulfillmentWindowEndDate) across the episode?
        // So we cant use episode startDate ASC as northstar does?
        ordering: { patient: { fullName: Ordering.ASC } },
    },
};

// Patients with visits assigned to an inactive clinician
// 1. A patient has visits with outcome = None assigned to a Clinician who is inactive
// 2. The visit's episode is Active or Pending with an end date today or later (FE-678)
// sorting: Sorted by visit dates with the earliest visit first
const INACTIVE_CLINICIAN_VISITS_CONTAINER: VisitDashboardContainer = {
    key: PINNACLE_CONTAINER_KEYS.inactiveClinicianVisits,
    label: 'Inactive Clinicians',
    defaultEnabled: true,
    hoverText: 'Visits assigned to an inactive clinician.',
    heavyQuery: true,
    snoozable: true,
    tableConfig: {
        entity: RootEntity.VISIT,
        highlightLinkedVisit: true,
        filters: {
            outcome: { exact: VisitOutcome.NONE },
            assignedClinicians: { isActive: { exact: false } },
            episode: EPISODE_ACTIVE_PENDING_PRESENT_OR_FUTURE_FILTER,
            effectiveDate: {
                gte: GraphInputHelpers.date(
                    new Date(Date.now() - DEFAULT_CONTAINER_FILTER_WINDOW_DAYS * ONE_DAY_MS),
                ),
            },
        },
        rowToLink: defaultEpisodeVisitsRowToLink,
        emptyTableText: 'No visits assigned to inactive clinicians.',
        ordering: { effectiveSortDate: Ordering.ASC },
    },
};

// Pending visits whose associated frequency has been discontinued
// 1. A patient's frequency order has been Discontinued
// 2. The patient has visits tied to that frequency with outcome = None
// sorting: Visits are sorted by date with the soonest visits listed first
const DISCONTINUED_FREQUENCIES_WITH_PENDING_VISITS_CONTAINER: VisitDashboardContainer = {
    key: PINNACLE_CONTAINER_KEYS.discontinuedFrequenciesWithPendingVisits,
    label: 'Discontinued Frequencies with Pending Visits',
    defaultEnabled: false,
    hoverText: 'Discontinued frequencies with scheduled visits that need to be cancelled.',
    heavyQuery: true,
    extraProps: { bulkActions: [ContainerBulkAction.CANCEL_VISITS] },
    snoozable: true,
    tableConfig: {
        entity: RootEntity.VISIT,
        highlightLinkedVisit: true,
        filters: {
            patient: { isActive: { exact: true } },
            outcome: { exact: VisitOutcome.NONE },
            relatedOrder: {
                discontinuedAt: { lt: GraphInputHelpers.date(new Date()) },
            },
        },
        rowToLink: defaultEpisodeVisitsRowToLink,
        emptyTableText: 'No visits tied to discontinued frequencies.',
        ordering: { effectiveSortDate: Ordering.ASC },
    },
};

// Patients in transfer status with scheduled visits
// 1. A patient's episode status = On Hold
// 2. The on-hold episode has pending future visits (outcome = None,
//    effectiveDate >= today)
// 3. The visit's service code is NOT in TRANSFER_RESOLUTION_SERVICE_IDS
//    (visits that end the transfer, like ROC or final OASIS Discharge —
//    they shouldn't be flagged as "needs cancellation").
// 4. The visit's service code is NOT in POST_DISCHARGE_EXCLUDED_SERVICE_IDS
//    (admin / non-billable items that don't need cancellation).
// Modeled as a visit-level queue so it surfaces the count of pending visits to
// cancel and supports bulk cancel; the episode-status condition is expressed via
// the visit's `episode` filter.
// sorting: Visits are sorted by date with the soonest visits listed first
const TRANSFERRED_PATIENTS_WITH_PENDING_VISITS_CONTAINER: VisitDashboardContainer = {
    key: PINNACLE_CONTAINER_KEYS.transferredPatientsWithPendingVisits,
    label: 'Transferred Patients with Pending Visits',
    defaultEnabled: false,
    hoverText: 'Transferred patients with scheduled visits that need to be cancelled.',
    heavyQuery: true,
    extraProps: {
        bulkActions: [ContainerBulkAction.CANCEL_VISITS],
    },
    snoozable: true,
    tableConfig: {
        entity: RootEntity.VISIT,
        highlightLinkedVisit: true,
        filters: {
            patient: { isActive: { exact: true } },
            outcome: { exact: VisitOutcome.NONE },
            effectiveDate: { gte: GraphInputHelpers.date(new Date()) },
            episode: { status: { exact: EpisodeStatus.ON_HOLD } },
            NOT: {
                services: {
                    externalIds: {
                        value: {
                            inList: [
                                ...TRANSFER_RESOLUTION_SERVICE_IDS,
                                ...POST_DISCHARGE_EXCLUDED_SERVICE_IDS,
                            ],
                        },
                    },
                },
            },
        },
        rowToLink: defaultEpisodeVisitsRowToLink,
        emptyTableText: 'No transfers requiring attention.',
        ordering: { effectiveSortDate: Ordering.ASC },
    },
};

// Patients that are in the transfer status that need to be monitored by the AA
// 1. An episode for a patient has Episode Status = On Hold AND today falls
//    within the episode's date window.
// sorting: Patients sorted by duration of transfer status, with the oldest first
// TODO (ask Jessica) (sorting) no on hold duration sort.
const ALL_TRANSFERRED_PATIENTS_CONTAINER: EpisodeOfCareDashboardContainer = {
    key: PINNACLE_CONTAINER_KEYS.allTransferredPatients,
    label: 'All Transferred Patients',
    defaultEnabled: false,
    heavyQuery: true,
    hoverText: 'All patients currently in transfer status.',
    snoozable: true,
    tableConfig: {
        entity: RootEntity.EPISODE_OF_CARE,
        filters: {
            patient: { isActive: { exact: true } },
            status: { exact: EpisodeStatus.ON_HOLD },
            isActiveByDate: { exact: true },
        },
        rowToLink: defaultEpisodeOfCareRowToLink,
        emptyTableText: 'No episodes pending in transfer.',
        ordering: { patient: { fullName: Ordering.ASC } },
    },
};

// Patients with an OASIS discharge who still have visits scheduled after
// 1. A patient has a OASIS Discharge scheduled with an outcome = None or Completed
// 2. The patient has visits with outcome = None and a date that is greater
//    than the date of the OASIS discharge visit
// 3. The discharge visit's episode is Active and currently includes today (FE-678)
// sorting: Sorted by earliest date of upcoming OASIS Discharge visit
const UPCOMING_DISCHARGE_WITH_FUTURE_PENDING_VISITS_CONTAINER: VisitDashboardContainer = {
    key: PINNACLE_CONTAINER_KEYS.upcomingDischargeWithFuturePendingVisits,
    label: 'Upcoming Discharge With Future Pending Visits',
    defaultEnabled: false,
    hoverText:
        'Patients with an upcoming OASIS discharge who still have other visits scheduled after the discharge date.',
    heavyQuery: true,
    snoozable: true,
    tableConfig: {
        entity: RootEntity.VISIT,
        highlightLinkedVisit: true,
        filters: {
            services: {
                externalIds: {
                    value: {
                        inList: OASIS_DISCHARGE_SERVICE_IDS,
                    },
                },
            },
            outcome: { inList: [VisitOutcome.NONE, VisitOutcome.COMPLETED] },
            episode: EPISODE_ACTIVE_PRESENT_OR_FUTURE_FILTER,
            // There exists a future visit on the same patient scheduled after
            // this discharge, excluding admin / non-billable items.
            relatedVisitCondition: {
                exists: true,
                samePatient: true,
                filter: {
                    NOT: {
                        services: {
                            externalIds: {
                                value: { inList: POST_DISCHARGE_EXCLUDED_SERVICE_IDS },
                            },
                        },
                    },
                    outcome: { exact: VisitOutcome.NONE },
                    effectiveDate: { gte: GraphInputHelpers.date(new Date()) },
                },
                timeConstraints: [
                    {
                        field: VisitDatetimeField.EFFECTIVE_DATE,
                        relativeTo: VisitDatetimeField.EFFECTIVE_DATE,
                        gtOffsetMinutes: 0,
                    },
                ],
            },
        },
        rowToLink: defaultEpisodeVisitsRowToLink,
        emptyTableText: 'No patients with visits after discharge.',
        ordering: { effectiveSortDate: Ordering.ASC },
    },
};

// Patients with more than 1 OASIS discharge or recert visit on an episode
// 1. An episode for a patient has more than 1 visit from the list of service
//    codes below with outcome = None or Completed
// sorting: Sorted alphabetically by patient name
const CONFLICTING_DISCHARGE_AND_RECERT_VISITS_CONTAINER: EpisodeOfCareDashboardContainer = {
    key: PINNACLE_CONTAINER_KEYS.conflictingDischargeAndRecertVisits,
    label: 'Conflicting Discharge and Recert Visits',
    defaultEnabled: false,
    heavyQuery: true,
    hoverText: 'Patients with more than 1 OASIS discharge or recert visit on an episode.',
    snoozable: true,
    tableConfig: {
        entity: RootEntity.EPISODE_OF_CARE,
        filters: {
            ...EPISODE_ACTIVE_PRESENT_OR_FUTURE_FILTER,
            hasVisits: {
                services: {
                    externalIds: {
                        value: {
                            inList: [...OASIS_DISCHARGE_SERVICE_IDS, ...OASIS_RECERT_SERVICE_IDS],
                        },
                    },
                },
                outcome: { inList: [VisitOutcome.NONE, VisitOutcome.COMPLETED] },
                relatedVisitCondition: {
                    exists: true,
                    sameEpisode: true,
                    filter: {
                        services: {
                            externalIds: {
                                value: {
                                    inList: [
                                        ...OASIS_DISCHARGE_SERVICE_IDS,
                                        ...OASIS_RECERT_SERVICE_IDS,
                                    ],
                                },
                            },
                        },
                        outcome: { inList: [VisitOutcome.NONE, VisitOutcome.COMPLETED] },
                    },
                },
            },
        },
        rowToLink: defaultEpisodeOfCareRowToLink,
        emptyTableText: 'No episodes with multiple OASIS Discharge or Recert visits.',
        ordering: { patient: { fullName: Ordering.ASC } },
    },
};

// Patients that are overscheduled in a frequency
// 1. A patient has a frequency order where the number of visits ordered is less
//    than the number of visits associated to that order with outcome = None or
//    Completed or Missed
// 2. The order's episode is Active and currently includes today (FE-678)
// sorting: Patients sorted by visit date with the soonest overscheduled visit first
const OVERSCHEDULED_PATIENTS_CONTAINER: OrderDashboardContainer = {
    key: PINNACLE_CONTAINER_KEYS.overscheduledPatients,
    label: 'Overscheduled Patients',
    defaultEnabled: true,
    hoverText: 'Patients who have an overscheduled frequency.',
    heavyQuery: true,
    snoozable: true,
    tableConfig: {
        entity: RootEntity.ORDER,
        filters: {
            patient: { isActive: { exact: true } },
            orderType: { exact: OrderType.FREQUENCY },
            exceedsMaxActiveOccurrences: { exact: true },
            episode: EPISODE_ACTIVE_PRESENT_OR_FUTURE_FILTER,
            // TODO (INT-980): remove this hacky filter once interop is no-longer associating
            // discharge summary, transfer services with orders
            NOT: {
                activeVisits: {
                    services: {
                        externalIds: {
                            value: {
                                inList: [...DISCHARGE_SUMMARY_SERVICE_IDS, ...TRANSFER_SERVICE_IDS],
                            },
                        },
                    },
                },
            },
        },
        rowToLink: defaultEpisodeOrdersRowToLink,
        emptyTableText: 'No overscheduled patients.',
        ordering: { fulfillmentWindowStartDate: Ordering.ASC },
    },
};

// Frequency orders that need review — fewer active visits than the order requires.
// 1. A patient has a frequency order where the number of visits ordered is
//    greater than the number of active visits associated to that order.
// 2. The order's episode is Active and currently includes today (FE-678)
// sorting: Patients sorted by fulfillment-window start date, soonest first
const UNDERSCHEDULED_PATIENTS_CONTAINER: OrderDashboardContainer = {
    key: PINNACLE_CONTAINER_KEYS.underscheduledPatients,
    label: 'Frequency Review Needed',
    defaultEnabled: true,
    hoverText: 'Frequency orders with fewer active visits than the order requires.',
    heavyQuery: true,
    snoozable: true,
    tableConfig: {
        entity: RootEntity.ORDER,
        filters: {
            patient: { isActive: { exact: true } },
            orderType: { exact: OrderType.FREQUENCY },
            exceedsMinActiveOccurrences: { exact: true },
            episode: EPISODE_ACTIVE_PRESENT_OR_FUTURE_FILTER,
            fulfillmentWindowEndDate: {
                gt: GraphInputHelpers.date(PINNACLE_FULFILLMENT_WINDOW_END_CUTOFF),
            },
        },
        rowToLink: defaultEpisodeOrdersRowToLink,
        emptyTableText: 'No frequency orders needing review.',
        ordering: { fulfillmentWindowStartDate: Ordering.ASC },
    },
};

// Per-discipline branch for the Pending Re-Eval queue. Each branch is a complete
// PatientFilter; we OR three of these together (PT/OT/ST) at the top level so a
// patient lights up if ANY one discipline is missing coverage.
const THERAPY_DISCIPLINE_DISCHARGE_SERVICE_IDS_MAP: Record<'PT' | 'OT' | 'ST', string[]> = {
    PT: PT_DISCIPLINE_DISCHARGE_SERVICE_IDS,
    OT: OT_DISCIPLINE_DISCHARGE_SERVICE_IDS,
    ST: ST_DISCIPLINE_DISCHARGE_SERVICE_IDS,
};

// Matches a future scheduled discharge (OASIS Discharge or per discipline
// Discharge Summary) in the given therapy discipline. Used to exclude
// patients who are already on their way out from the supe / re eval queues.
const pendingDischargeVisitFilter = (disciplineCode: 'PT' | 'OT' | 'ST'): VisitFilter => ({
    services: {
        externalIds: {
            value: {
                inList: [
                    ...OASIS_DISCHARGE_SERVICE_IDS,
                    ...THERAPY_DISCIPLINE_DISCHARGE_SERVICE_IDS_MAP[disciplineCode],
                ],
            },
        },
    },
    disciplines: { code: { exact: disciplineCode } },
    outcome: { exact: VisitOutcome.NONE },
    effectiveDate: { gte: GraphInputHelpers.date(new Date()) },
});

const pendingReEvalBranchForDiscipline = (disciplineCode: 'PT' | 'OT' | 'ST'): PatientFilter => ({
    isActive: { exact: true },
    // Patient has at least one ACTIVE-status episode currently in its date window or in the future.
    hasEpisodes: EPISODE_ACTIVE_PRESENT_OR_FUTURE_FILTER,
    // Anchor: patient has a future, pending visit in this discipline.
    hasVisits: {
        disciplines: { code: { exact: disciplineCode } },
        outcome: { exact: VisitOutcome.NONE },
        effectiveDate: { gte: GraphInputHelpers.date(new Date()) },
    },
    AND: {
        // Brand new patient gate: the cadence doesn't start until the patient
        // has had at least one completed visit in this discipline *within the
        // current active episode*.
        hasVisits: {
            disciplines: { code: { exact: disciplineCode } },
            outcome: { exact: VisitOutcome.COMPLETED },
            episode: EPISODE_ACTIVE_PRESENT_OR_FUTURE_FILTER,
        },
        // No completed reassessment in same discipline in the last 23 days.
        NOT: {
            hasVisits: {
                services: {
                    externalIds: { value: { inList: REASSESSMENT_SERVICE_IDS } },
                },
                disciplines: { code: { exact: disciplineCode } },
                outcome: { exact: VisitOutcome.COMPLETED },
                effectiveDate: {
                    gte: GraphInputHelpers.date(new Date(Date.now() - 23 * ONE_DAY_MS)),
                },
            },
        },
        AND: {
            // No scheduled, fully-assigned reassessment in this discipline within the next 7 days.
            NOT: {
                hasVisits: {
                    services: {
                        externalIds: { value: { inList: REASSESSMENT_SERVICE_IDS } },
                    },
                    disciplines: { code: { exact: disciplineCode } },
                    outcome: { exact: VisitOutcome.NONE },
                    effectiveDate: {
                        gte: GraphInputHelpers.date(new Date()),
                        lte: GraphInputHelpers.date(new Date(Date.now() + 7 * ONE_DAY_MS)),
                    },
                    needsAssignment: { exact: false },
                },
            },
            AND: {
                // No pending discharge in this discipline.
                NOT: { hasVisits: pendingDischargeVisitFilter(disciplineCode) },
            },
        },
    },
});

// Patients needing a PT, OT, or ST reassessment visit
// Inclusion Criteria
// 1. Patient has a future visit (outcome = None) in PT, OT, or ST.
// 2. Patient has at least one COMPLETED visit in that discipline in the
//    currently active episode (brand-new-patient gate. Completed visits
//    from a prior closed episode don't carry over).
// 3. Patient does NOT have a completed reassessment-coded visit (re-eval,
//    SOC/ROC/Recert/Eval, discharge, death) in that discipline within the
//    last 23 days.
// 4. Patient does NOT have a future fully-assigned (not missing service,
//    clinician, or date) reassessment scheduled in that discipline within
//    the next 7 days.
// 5. Patient does NOT have a pending discharge in that discipline.
// Sorting: Alphabetical by patient full name.
const PENDING_THERAPY_REEVALUATION_CONTAINER: PatientDashboardContainer = {
    key: PINNACLE_CONTAINER_KEYS.pendingTherapyReevaluation,
    label: 'Pending Therapy Re-Evaluation Visits',
    defaultEnabled: false,
    heavyQuery: true,
    hoverText:
        'Patients needing a PT, OT, or ST re-eval within 30 days of their last reassessment.',
    snoozable: true,
    tableConfig: {
        entity: RootEntity.PATIENT,
        filters: {
            ...pendingReEvalBranchForDiscipline('PT'),
            OR: {
                ...pendingReEvalBranchForDiscipline('OT'),
                OR: {
                    ...pendingReEvalBranchForDiscipline('ST'),
                },
            },
        },
        ordering: { fullName: Ordering.ASC },
        rowToLink: ({ id }: { id: string }) => `/patients/${id}/scheduling`,
        emptyTableText: 'No patients pending re-evaluation.',
    },
};

// Shared "pending supervisory" filter shape used by the HHA, LPN, and Therapy supe wqs.
// A patient appears in the queue if all of the following:
//   1. They have a future, pending visit in the anchor service-code list
//      (outcome = None, effectiveDate >= today).
//   2. They have NO completed visit in the supervisory service-code list
//      within the look-back window.
//   3. They have NO future scheduled, fully-assigned supervisory visit
//      "already covering" the patient. `upcomingSupeWithin` controls what
//      "covering" means:
//        - { daysAfterPriorSupe }: HHA/LPN cadence: a future supe counts as
//          coverage iff its effectiveDate falls within N days AFTER the
//          effectiveDate of a prior supervisory visit on the same list.
//        - { daysFromToday }: Therapy cadence: a future supe
//          counts as coverage if its effectiveDate falls in [today, today+N].
type UpcomingSupeWithin = { daysAfterPriorSupe: number } | { daysFromToday: number };

// "Already covered" check: a future scheduled, fully-assigned supe visit that
// disqualifies the patient from the queue. Two coverage shapes:
//   - daysAfterPriorSupe (HHA/LPN cadence): future supe whose effectiveDate is
//     within N days AFTER a prior supe (any episode, same patient).
//   - daysFromToday (Therapy cadence): future supe whose effectiveDate falls
//     within [today, today+N].
const buildUpcomingSupeCoverageFilter = (
    supervisoryServiceIds: string[],
    disciplineFilter: VisitFilter,
    upcomingSupeWithin: UpcomingSupeWithin,
    today: string,
): VisitFilter => {
    const supervisoryServicesFilter = {
        services: { externalIds: { value: { inList: supervisoryServiceIds } } },
    };
    if ('daysAfterPriorSupe' in upcomingSupeWithin) {
        return {
            ...supervisoryServicesFilter,
            ...disciplineFilter,
            outcome: { exact: VisitOutcome.NONE },
            effectiveDate: { gte: today },
            needsAssignment: { exact: false },
            relatedVisitCondition: {
                exists: true,
                samePatient: true,
                sameEpisode: false,
                filter: { ...supervisoryServicesFilter, ...disciplineFilter },
                timeConstraints: [
                    {
                        field: VisitDatetimeField.EFFECTIVE_DATE,
                        relativeTo: VisitDatetimeField.EFFECTIVE_DATE,
                        gteOffsetMinutes: -upcomingSupeWithin.daysAfterPriorSupe * 24 * 60,
                        lteOffsetMinutes: 0,
                    },
                ],
            },
        };
    }
    return {
        ...supervisoryServicesFilter,
        ...disciplineFilter,
        outcome: { exact: VisitOutcome.NONE },
        effectiveDate: {
            gte: today,
            lte: GraphInputHelpers.date(
                new Date(Date.now() + upcomingSupeWithin.daysFromToday * ONE_DAY_MS),
            ),
        },
        needsAssignment: { exact: false },
    };
};

// anchorServiceIds: The trigger visit. Patient has a future PTA/COTA visit scheduled.
// supervisoryServiceIds: The actual supe visit codes. Used for both the
//   lookback window and the upcoming-supe coverage check.
const pendingSupervisoryBranch = ({
    anchorServiceIds,
    supervisoryServiceIds,
    lookbackDays,
    upcomingSupeWithin,
    disciplineCode,
}: {
    anchorServiceIds: string[];
    supervisoryServiceIds: string[];
    lookbackDays: number;
    upcomingSupeWithin: UpcomingSupeWithin;
    disciplineCode?: 'PT' | 'OT';
}): PatientFilter => {
    const disciplineFilter: VisitFilter = disciplineCode
        ? { disciplines: { code: { exact: disciplineCode } } }
        : {};
    const today = GraphInputHelpers.date(new Date());
    const upcomingSupeFilter = buildUpcomingSupeCoverageFilter(
        supervisoryServiceIds,
        disciplineFilter,
        upcomingSupeWithin,
        today,
    );
    return {
        isActive: { exact: true },
        // Anchor: patient has a future, pending visit in the anchor service list.
        hasVisits: {
            services: { externalIds: { value: { inList: anchorServiceIds } } },
            outcome: { exact: VisitOutcome.NONE },
            effectiveDate: { gte: today },
        },
        // No completed matching supervisory in the look-back window.
        NOT: {
            hasVisits: {
                services: { externalIds: { value: { inList: supervisoryServiceIds } } },
                ...disciplineFilter,
                outcome: { exact: VisitOutcome.COMPLETED },
                effectiveDate: {
                    gte: GraphInputHelpers.date(new Date(Date.now() - lookbackDays * ONE_DAY_MS)),
                },
            },
        },
        AND: {
            NOT: { hasVisits: upcomingSupeFilter },
            ...(disciplineCode
                ? {
                      AND: {
                          // No pending discharge in this discipline.
                          NOT: { hasVisits: pendingDischargeVisitFilter(disciplineCode) },
                      },
                  }
                : {}),
        },
    };
};

// Pending Therapy Supervisory Visits (PRD-619)
// 1. A patient has a future PTA or COTA visit in the service codes listed
//    below with outcome = 'None'.
// 2. The patient does not have a completed supervisory visit from the list of
//    service codes below for that discipline more recently than 23 days ago.
// 3. There is no scheduled supervisory visit (not missing service, clinician,
//    or date) from the list of service codes below within the next 7 days for
//    that patient.
// Sorting: Sorted by days since the previous reassessment visit, with the
// highest number first.
// TODO (sorting): days-since-previous-reassessment sort needs a derived BE field.
const PENDING_THERAPY_SUPERVISORY_CONTAINER: PatientDashboardContainer = {
    key: PINNACLE_CONTAINER_KEYS.pendingTherapySupervisory,
    label: 'Pending Therapy Supervisory Visits',
    defaultEnabled: false,
    heavyQuery: true,
    hoverText: 'Patients needing a PT or OT Supervisory visit within the next 7 days.',
    snoozable: true,
    tableConfig: {
        entity: RootEntity.PATIENT,
        filters: {
            ...pendingSupervisoryBranch({
                anchorServiceIds: PTA_VISIT_SERVICE_IDS,
                supervisoryServiceIds: PT_THERAPY_SUPERVISORY_SERVICE_IDS,
                lookbackDays: 23,
                upcomingSupeWithin: { daysFromToday: 7 },
                disciplineCode: 'PT',
            }),
            OR: {
                ...pendingSupervisoryBranch({
                    anchorServiceIds: OTA_VISIT_SERVICE_IDS,
                    supervisoryServiceIds: OT_THERAPY_SUPERVISORY_SERVICE_IDS,
                    lookbackDays: 23,
                    upcomingSupeWithin: { daysFromToday: 7 },
                    disciplineCode: 'OT',
                }),
            },
        },
        ordering: { fullName: Ordering.ASC },
        rowToLink: ({ id }: { id: string }) => `/patients/${id}/scheduling?episode=all`,
        emptyTableText: 'No patients pending therapy supervisory visits.',
    },
};

// Pending HHA Supervisory visits
// 1. A patient has a future HHA visit in the service codes listed below
//    with outcome = 'None'.
// 2. A patient does not have a completed supervisory visit from the list of
//    service codes below more recently than 7 days ago.
// 3. The patient has a future scheduled supervisory visit on the list whose
//    effectiveDate is within 14 days AFTER the effectiveDate of a prior
//    supervisory visit on the list.
// 4. The patient has an Active episode currently in its date window (FE-678).
// Sorting: Sorted by earliest date of upcoming supervisory visit.
// TODO (sorting): earliest-upcoming-supervisory-visit sort needs a derived BE
// field.
const PENDING_HHA_SUPERVISORY_CONTAINER: PatientDashboardContainer = {
    key: PINNACLE_CONTAINER_KEYS.pendingHhaSupervisory,
    label: 'Pending HHA Supervisory Visits',
    defaultEnabled: false,
    heavyQuery: true,
    hoverText:
        'Patients needing an HHA Supervisory visit within 14 days of their last supervisory event.',
    snoozable: true,
    tableConfig: {
        entity: RootEntity.PATIENT,
        filters: {
            hasEpisodes: EPISODE_ACTIVE_PRESENT_OR_FUTURE_FILTER,
            ...pendingSupervisoryBranch({
                anchorServiceIds: HHA_VISIT_SERVICE_IDS,
                supervisoryServiceIds: HHA_SUPERVISORY_SERVICE_IDS,
                lookbackDays: 7,
                upcomingSupeWithin: { daysAfterPriorSupe: 14 },
            }),
        },
        ordering: { fullName: Ordering.ASC },
        rowToLink: ({ id }: { id: string }) => `/patients/${id}/scheduling?episode=all`,
        emptyTableText: 'No patients pending HHA supervisory visits.',
    },
};

// All Patients who are active but don't have any future visits scheduled
// 1. A patient has status = Active AND at least one episode with status = ACTIVE.
//    Patients whose most recent episode is ON_HOLD / DISCHARGED / etc. should
//    not appear here even if patient.isActive is still true (ENG-8608).
// 2. The patient has no pending visit (outcome = None), ignoring non-visit
//    tasks. Admin NVTs like Patient Consent or CMS 485 do not count as a
//    visit; OASIS death and transfer assessments do count.
// sorting: Sorted by visit dates with the earliest visit first
// TODO: PRD wants sort by "visit dates, earliest first" (over the patient's
// non-pending historical visits). PatientOrdering only exposes patient-level
// fields, so no BE-supported equivalent today — same gap as the other PATIENT
// containers in this file.
const ACTIVE_PATIENTS_WITH_NO_VISITS_CONTAINER: PatientDashboardContainer = {
    key: PINNACLE_CONTAINER_KEYS.activePatientsWithNoVisits,
    label: 'Active Patients with No Visits',
    defaultEnabled: false,
    hoverText: "Patients who are active but don't have any future visits scheduled.",
    heavyQuery: true,
    snoozable: true,
    tableConfig: {
        entity: RootEntity.PATIENT,
        filters: {
            isActive: { exact: true },
            hasEpisodes: EPISODE_ACTIVE_PRESENT_OR_FUTURE_FILTER,
            NOT: {
                hasVisits: {
                    outcome: { exact: VisitOutcome.NONE },
                    // Ignore admin non-visit tasks so they do not count as a
                    // pending visit. OASIS death/transfer visits still count.
                    NOT: {
                        services: {
                            externalIds: {
                                value: { inList: NON_VISIT_TASK_ONLY_SERVICE_IDS },
                            },
                        },
                    },
                },
            },
        },
        ordering: { fullName: Ordering.ASC },
        rowToLink: ({ id }: { id: string }) => `/patients/${id}/scheduling`,
        emptyTableText: 'No active patients without pending visits.',
    },
};

// Pending LPN Supervisory visits (per ENG-8607)
// 1. A patient has a future LPN visit in the service codes listed below
//    with outcome = 'None'.
// 2. A patient does not have a completed supervisory visit from the list of
//    service codes below more recently than 23 days ago.
// 3. The patient has a future scheduled supervisory visit on the list (not
//    missing service, clinician, or date) whose effectiveDate is within 30
//    days AFTER the effectiveDate of a prior supervisory visit on the list.
// 4. The patient has an Active episode currently in its date window (FE-678).
// Sorting: Sorted by earliest date of upcoming supervisory visit.
// TODO (sorting): earliest-upcoming-supervisory-visit sort needs a derived BE
// field.
const PENDING_LPN_SUPERVISORY_CONTAINER: PatientDashboardContainer = {
    key: PINNACLE_CONTAINER_KEYS.pendingLpnSupervisory,
    label: 'Pending LPN Supervisory Visits',
    defaultEnabled: false,
    heavyQuery: true,
    hoverText:
        'Patients needing an LPN Supervisory visit within 30 days of their last supervisory event.',
    snoozable: true,
    tableConfig: {
        entity: RootEntity.PATIENT,
        filters: {
            hasEpisodes: EPISODE_ACTIVE_PRESENT_OR_FUTURE_FILTER,
            ...pendingSupervisoryBranch({
                anchorServiceIds: LPN_VISIT_SERVICE_IDS,
                supervisoryServiceIds: LPN_SUPERVISORY_SERVICE_IDS,
                lookbackDays: 23,
                upcomingSupeWithin: { daysAfterPriorSupe: 30 },
            }),
        },
        ordering: { fullName: Ordering.ASC },
        rowToLink: ({ id }: { id: string }) => `/patients/${id}/scheduling?episode=all`,
        emptyTableText: 'No patients pending LPN supervisory visits.',
    },
};

// Unresolved Episodes
// 1. A patient has an episode whose end date is before today with episode
//    status = 'Active' or 'On Hold'
// Sorting: Sorted by episode end date, earliest date first.
const UNRESOLVED_EPISODES_CONTAINER: EpisodeOfCareDashboardContainer = {
    key: PINNACLE_CONTAINER_KEYS.unresolvedEpisodes,
    label: 'Unresolved Episodes',
    defaultEnabled: true,
    hoverText:
        'Active or on-hold episodes whose end date has already passed — need a recert or discharge.',
    snoozable: true,
    tableConfig: {
        entity: RootEntity.EPISODE_OF_CARE,
        filters: {
            patient: { isActive: { exact: true } },
            endDate: { lt: TODAY },
            status: { inList: [EpisodeStatus.ACTIVE, EpisodeStatus.ON_HOLD] },
        },
        rowToLink: defaultEpisodeOfCareRowToLink,
        emptyTableText: 'No unresolved episodes.',
        ordering: { endDate: Ordering.ASC },
    },
};

// Pinnacle-specific overrides for HH-shared containers.
// Per the FE-678 workqueues guide, the episode gate is Active or Pending with
// an end date today or later (was: Active + currently-in-window).
const PINNACLE_INCOMPLETE_CARE_TEAM_CONTAINER: PatientDashboardContainer = {
    key: PINNACLE_CONTAINER_KEYS.incompleteCareTeam,
    label: 'Incomplete Care Team',
    defaultEnabled: false,
    hoverText: 'Patients that need care teams assigned.',
    snoozable: true,
    tableConfig: {
        entity: RootEntity.PATIENT,
        filters: {
            isActive: { exact: true },
            hasEpisodes: {
                ...EPISODE_ACTIVE_PENDING_PRESENT_OR_FUTURE_FILTER,
                hasVisits: {
                    disciplineCoveredByCareTeam: { exact: false },
                    outcome: { exact: VisitOutcome.NONE },
                    NOT: {
                        disciplines: {
                            externalIds: { value: { inList: ['MISC', 'ORDERS', 'COMM'] } },
                        },
                    },
                },
            },
        },
        rowToLink: ({ id }: { id: string }) => `/patients/${id}/scheduling`,
        emptyTableText: 'No patients with incomplete care teams.',
        // TODO(BE-304): Spec wants "sort by number of days since SOC
        // completion date / episode start date, oldest first". That
        // requires sorting patients by a property of a filtered subset
        // of their visits. Using fullName ASC as a placeholder until
        // BE-304 lands.
        ordering: { fullName: Ordering.ASC },
    },
};

const POTENTIAL_RESCHEDULES_CONTAINER: VisitDashboardContainer = {
    ...HH_PATIENT_NO_SHOWS_CONTAINER,
    key: PINNACLE_CONTAINER_KEYS.potentialReschedules,
    label: 'Potential Reschedules',
    hoverText: 'Visits that need to be either rescheduled or marked as missed.',
    snoozable: true,
    tableConfig: {
        ...HH_PATIENT_NO_SHOWS_CONTAINER.tableConfig,
        ordering: { effectiveSortDate: Ordering.DESC },
    },
};

// Agency-discharged (inactive) patients who still have pending future visits.
// 1. The patient has been discharged from the agency (isActive = false)
// 2. The patient has visits with outcome = None and effectiveDate >= today
// 3. The visit's service code is NOT in POST_DISCHARGE_EXCLUDED_SERVICE_IDS
//    (admin / non-billable items that don't need cancellation).
// Modeled as a visit-level queue so it surfaces the count of pending visits to
// cancel rather than the count of patients, and supports bulk cancel.
// sorting: Visits are sorted by date with the soonest visits listed first
const AGENCY_DISCHARGED_PATIENTS_WITH_PENDING_VISITS_CONTAINER: VisitDashboardContainer = {
    key: PINNACLE_CONTAINER_KEYS.agencyDischargedPatientsWithPendingVisits,
    label: 'Agency Discharged Patients with Pending Visits',
    defaultEnabled: false,
    hoverText: 'Agency discharged patients with scheduled visits that need to be cancelled.',
    heavyQuery: true,
    extraProps: { bulkActions: [ContainerBulkAction.CANCEL_VISITS] },
    snoozable: true,
    tableConfig: {
        entity: RootEntity.VISIT,
        highlightLinkedVisit: true,
        filters: {
            patient: { isActive: { exact: false } },
            outcome: { exact: VisitOutcome.NONE },
            effectiveDate: { gte: GraphInputHelpers.date(new Date()) },
            NOT: {
                services: {
                    externalIds: {
                        value: {
                            inList: [
                                ...OASIS_DISCHARGE_SERVICE_IDS,
                                ...POST_DISCHARGE_EXCLUDED_SERVICE_IDS,
                            ],
                        },
                    },
                },
            },
        },
        rowToLink: defaultEpisodeVisitsRowToLink,
        emptyTableText: 'No agency discharged patients with pending visits.',
        ordering: { effectiveSortDate: Ordering.ASC },
    },
};

// Visits Assigned to Contract Agency
// A visit has outcome = NONE
// and is assigned to a clinician with an external ID whose type is CONTRACT_AGENCY
const VISITS_ASSIGNED_TO_CONTRACT_AGENCY_CONTAINER: VisitDashboardContainer = {
    key: PINNACLE_CONTAINER_KEYS.visitsAssignedToContractAgency,
    label: 'Visits Assigned to Contract Agency',
    defaultEnabled: false,
    hoverText: 'Visits sent to a contract agency with no individual contractor yet assigned.',
    heavyQuery: true,
    snoozable: true,
    tableConfig: {
        entity: RootEntity.VISIT,
        highlightLinkedVisit: true,
        filters: {
            outcome: { exact: VisitOutcome.NONE },
            assignedClinicians: {
                externalIds: {
                    type: { exact: 'CONTRACT_AGENCY' },
                },
            },
        },
        rowToLink: defaultVisitsRowToLink,
        emptyTableText: 'No visits assigned to a contract agency.',
        ordering: { effectiveSortDate: Ordering.ASC },
    },
};

const ALL_DISCIPLINE_DISCHARGE_SERVICE_IDS = [
    ...OT_DISCIPLINE_DISCHARGE_SERVICE_IDS,
    ...PT_DISCIPLINE_DISCHARGE_SERVICE_IDS,
    ...SN_DISCIPLINE_DISCHARGE_SERVICE_IDS,
    ...ST_DISCIPLINE_DISCHARGE_SERVICE_IDS,
    ...MSW_DISCIPLINE_DISCHARGE_SERVICE_IDS,
];

// Completed Eval or ROC in the same discipline "re-admits" the patient and
// nullifies a prior discharge for FE-649.
const DISCIPLINE_READMISSION_SERVICE_IDS = [...EVAL_SERVICE_IDS, ...ROC_SERVICE_IDS];

// Discipline Discharge Patients with Pending Visits
// Row = a pending visit that needs canceling, NOT the discharge itself.
// Inclusion:
// 1. Visit has outcome = NONE.
// 2. The visit's service code is NOT in POST_DISCHARGE_EXCLUDED_SERVICE_IDS
// 3. The patient has a related completed discipline-discharge visit in the
//    same discipline whose effectiveDate is BEFORE this pending visit's, AND
//    that discharge has NOT been nullified by a later completed same-discipline
//    Eval or ROC.
// Sorting: Sorted by earliest date of upcoming visits.
const DISCIPLINE_DISCHARGE_PATIENTS_WITH_PENDING_VISITS_CONTAINER: VisitDashboardContainer = {
    key: PINNACLE_CONTAINER_KEYS.disciplineDischargePatientsWithPendingVisits,
    label: 'Discipline Discharge Patients with Pending Visits',
    defaultEnabled: false,
    hoverText: 'Discipline discharged patients with scheduled visits that need to be canceled.',
    heavyQuery: true,
    extraProps: { bulkActions: [ContainerBulkAction.CANCEL_VISITS] },
    snoozable: true,
    tableConfig: {
        entity: RootEntity.VISIT,
        highlightLinkedVisit: true,
        filters: {
            outcome: { exact: VisitOutcome.NONE },
            NOT: {
                services: {
                    externalIds: { value: { inList: POST_DISCHARGE_EXCLUDED_SERVICE_IDS } },
                },
            },
            relatedVisitCondition: {
                exists: true,
                samePatient: true,
                sameDiscipline: true,
                filter: {
                    services: {
                        externalIds: { value: { inList: ALL_DISCIPLINE_DISCHARGE_SERVICE_IDS } },
                    },
                    outcome: { exact: VisitOutcome.COMPLETED },
                    // Discharge is nullified if a later same-discipline Eval/ROC
                    // re-admitted the patient.
                    NOT: {
                        relatedVisitCondition: {
                            exists: true,
                            samePatient: true,
                            sameDiscipline: true,
                            filter: {
                                services: {
                                    externalIds: {
                                        value: {
                                            inList: DISCIPLINE_READMISSION_SERVICE_IDS,
                                        },
                                    },
                                },
                                outcome: { exact: VisitOutcome.COMPLETED },
                            },
                            timeConstraints: [
                                {
                                    field: VisitDatetimeField.EFFECTIVE_DATE,
                                    relativeTo: VisitDatetimeField.EFFECTIVE_DATE,
                                    gtOffsetMinutes: 0,
                                },
                            ],
                        },
                    },
                },
                timeConstraints: [
                    {
                        field: VisitDatetimeField.EFFECTIVE_DATE,
                        relativeTo: VisitDatetimeField.EFFECTIVE_DATE,
                        ltOffsetMinutes: 0,
                    },
                ],
            },
        },
        rowToLink: defaultEpisodeVisitsRowToLink,
        emptyTableText: 'No pending visits after discipline discharge.',
        ordering: { effectiveSortDate: Ordering.ASC },
    },
};

// All missed visits from this Medicare week.
// 1. Visits with outcome = 'Missed'.
// 2. effectiveDate falls in the current Medicare week (Sun-Sat).
// Sorting: by visit date, earliest first.
const MISSED_VISITS_CONTAINER: VisitDashboardContainer = {
    key: PINNACLE_CONTAINER_KEYS.missedVisits,
    label: 'Missed Visits',
    defaultEnabled: false,
    heavyQuery: true,
    hoverText: 'All missed visits scheduled in the current Medicare week.',
    snoozable: true,
    tableConfig: {
        entity: RootEntity.VISIT,
        filters: {
            outcome: { exact: VisitOutcome.MISSED },
            effectiveDate: {
                gte: CURRENT_MEDICARE_WEEK_START,
                lte: CURRENT_MEDICARE_WEEK_END,
            },
        },
        rowToLink: defaultVisitsRowToLink,
        emptyTableText: 'No missed visits this Medicare week.',
        ordering: { effectiveSortDate: Ordering.ASC },
    },
};

// Patients Missing CMS-485
// Patient qualifies if they have an active episode with a completed SOC and
// no satisfying CMS-485 task. Satisfied when a CMS-485 is completed (any
// date) OR scheduled on the same date as the SOC.
const PATIENTS_MISSING_CMS_485_CONTAINER: PatientDashboardContainer = {
    key: PINNACLE_CONTAINER_KEYS.patientsMissingCms485,
    label: 'Patients Missing CMS-485',
    defaultEnabled: false,
    hoverText: 'Patients missing a CMS-485 task same day as SOC.',
    heavyQuery: true,
    snoozable: true,
    tableConfig: {
        entity: RootEntity.PATIENT,
        filters: {
            isActive: { exact: true },
            hasEpisodes: {
                ...EPISODE_ACTIVE_PRESENT_OR_FUTURE_FILTER,
                hasVisits: {
                    services: { externalIds: { value: { inList: SOC_SERVICE_IDS } } },
                    outcome: { exact: VisitOutcome.COMPLETED },
                    // No completed CMS-485 on any date
                    relatedVisitCondition: {
                        exists: false,
                        samePatient: true,
                        sameEpisode: true,
                        filter: {
                            services: {
                                externalIds: { value: { exact: CMS_485_SERVICE_ID } },
                            },
                            outcome: { exact: VisitOutcome.COMPLETED },
                        },
                    },
                    AND: {
                        // No pending CMS-485 scheduled same-day as the SOC
                        relatedVisitCondition: {
                            exists: false,
                            samePatient: true,
                            sameEpisode: true,
                            filter: {
                                services: {
                                    externalIds: { value: { exact: CMS_485_SERVICE_ID } },
                                },
                                outcome: { exact: VisitOutcome.NONE },
                            },
                            timeConstraints: [
                                {
                                    field: VisitDatetimeField.EFFECTIVE_DATE,
                                    relativeTo: VisitDatetimeField.EFFECTIVE_DATE,
                                    gteOffsetMinutes: 0,
                                    lteOffsetMinutes: 0,
                                },
                            ],
                        },
                    },
                },
            },
        },
        ordering: { fullName: Ordering.ASC },
        rowToLink: ({ id }: { id: string }) => `/patients/${id}/scheduling`,
        emptyTableText: 'No patients missing CMS-485.',
    },
};

// Patients Missing NOMNCs
// Patient qualifies if they have an active episode with a scheduled discharge
// visit that lacks a paired NOMNC task within the 48h window prior to that
// discharge (same patient). The 48h window is anchored to the discharge
// visit's effectiveDate.
//
// The queue only flags patients whose discharge visit is less than 7 days
// away. Late in an episode patients can switch from a planned discharge to a
// recertification (which does not require a NOMNC), so far-future discharges
// are intentionally excluded until they are close enough to be actionable.
const PATIENTS_MISSING_NOMNC_CONTAINER: PatientDashboardContainer = {
    key: PINNACLE_CONTAINER_KEYS.patientsMissingNomnc,
    label: 'Patients Missing NOMNCs',
    defaultEnabled: false,
    hoverText: 'Patients missing NOMNC 48 hours before a discharge within 7 days.',
    heavyQuery: true,
    snoozable: true,
    tableConfig: {
        entity: RootEntity.PATIENT,
        filters: {
            isActive: { exact: true },
            hasEpisodes: {
                ...EPISODE_ACTIVE_PRESENT_OR_FUTURE_FILTER,
                hasVisits: {
                    services: {
                        externalIds: { value: { inList: OASIS_DISCHARGE_ONLY_SERVICE_IDS } },
                    },
                    outcome: { exact: VisitOutcome.NONE },
                    effectiveDate: {
                        lte: GraphInputHelpers.date(new Date(Date.now() + 7 * ONE_DAY_MS)),
                    },
                    NOT: {
                        relatedVisitCondition: {
                            exists: true,
                            samePatient: true,
                            sameEpisode: true,
                            filter: {
                                services: {
                                    externalIds: { value: { inList: NOMNC_SERVICE_IDS } },
                                },
                            },
                            timeConstraints: [
                                {
                                    field: VisitDatetimeField.EFFECTIVE_DATE,
                                    relativeTo: VisitDatetimeField.EFFECTIVE_DATE,
                                    gteOffsetMinutes: -48 * 60,
                                    lteOffsetMinutes: 0,
                                },
                            ],
                        },
                    },
                },
            },
        },
        ordering: { fullName: Ordering.ASC },
        rowToLink: ({ id }: { id: string }) => `/patients/${id}/scheduling`,
        emptyTableText: 'No patients missing NOMNCs.',
    },
};

// Patients Missing SCIC Orders
// Patient qualifies if they have an active episode with a completed
// OASIS-E2 Follow-Up and no satisfying SCIC Order. Satisfied when a SCIC
// Order is completed (any date) OR scheduled on the same date as the Follow-Up.
const PATIENTS_MISSING_SCIC_ORDER_CONTAINER: PatientDashboardContainer = {
    key: PINNACLE_CONTAINER_KEYS.patientsMissingScicOrder,
    label: 'Patients Missing SCIC Orders',
    defaultEnabled: false,
    hoverText: 'Patients missing a SCIC Order task same day as Follow-Up.',
    heavyQuery: true,
    snoozable: true,
    tableConfig: {
        entity: RootEntity.PATIENT,
        filters: {
            isActive: { exact: true },
            hasEpisodes: {
                ...EPISODE_ACTIVE_PRESENT_OR_FUTURE_FILTER,
                hasVisits: {
                    services: {
                        externalIds: { value: { inList: OASIS_E2_FOLLOWUP_SERVICE_IDS } },
                    },
                    outcome: { exact: VisitOutcome.COMPLETED },
                    // No completed SCIC Order on any date
                    relatedVisitCondition: {
                        exists: false,
                        samePatient: true,
                        sameEpisode: true,
                        filter: {
                            services: {
                                externalIds: { value: { exact: SCIC_ORDER_SERVICE_ID } },
                            },
                            outcome: { exact: VisitOutcome.COMPLETED },
                        },
                    },
                    AND: {
                        // No pending SCIC Order scheduled same-day as the Follow-Up
                        relatedVisitCondition: {
                            exists: false,
                            samePatient: true,
                            sameEpisode: true,
                            filter: {
                                services: {
                                    externalIds: { value: { exact: SCIC_ORDER_SERVICE_ID } },
                                },
                                outcome: { exact: VisitOutcome.NONE },
                            },
                            timeConstraints: [
                                {
                                    field: VisitDatetimeField.EFFECTIVE_DATE,
                                    relativeTo: VisitDatetimeField.EFFECTIVE_DATE,
                                    gteOffsetMinutes: 0,
                                    lteOffsetMinutes: 0,
                                },
                            ],
                        },
                    },
                },
            },
        },
        ordering: { fullName: Ordering.ASC },
        rowToLink: ({ id }: { id: string }) => `/patients/${id}/scheduling`,
        emptyTableText: 'No patients missing SCIC Orders.',
    },
};

// Patients Missing Post Hospital Order
// Patient qualifies if they have an active episode with a completed ROC and
// no satisfying Post Hospital Order. Satisfied when a Post Hospital Order is
// completed (any date) OR scheduled on the same date as the ROC.
const PATIENTS_MISSING_POST_HOSPITAL_ORDER_CONTAINER: PatientDashboardContainer = {
    key: PINNACLE_CONTAINER_KEYS.patientsMissingPostHospitalOrder,
    label: 'Patients Missing Post Hospital Orders',
    defaultEnabled: false,
    hoverText: 'Patients missing a Post Hospital Order same day as ROC.',
    heavyQuery: true,
    snoozable: true,
    tableConfig: {
        entity: RootEntity.PATIENT,
        filters: {
            isActive: { exact: true },
            hasEpisodes: {
                ...EPISODE_ACTIVE_PRESENT_OR_FUTURE_FILTER,
                hasVisits: {
                    services: { externalIds: { value: { inList: ROC_SERVICE_IDS } } },
                    outcome: { exact: VisitOutcome.COMPLETED },
                    // No completed Post Hospital Order on any date
                    relatedVisitCondition: {
                        exists: false,
                        samePatient: true,
                        sameEpisode: true,
                        filter: {
                            services: {
                                externalIds: {
                                    value: { exact: POST_HOSPITAL_ORDER_SERVICE_ID },
                                },
                            },
                            outcome: { exact: VisitOutcome.COMPLETED },
                        },
                    },
                    AND: {
                        // No pending Post Hospital Order scheduled same-day as the ROC
                        relatedVisitCondition: {
                            exists: false,
                            samePatient: true,
                            sameEpisode: true,
                            filter: {
                                services: {
                                    externalIds: {
                                        value: { exact: POST_HOSPITAL_ORDER_SERVICE_ID },
                                    },
                                },
                                outcome: { exact: VisitOutcome.NONE },
                            },
                            timeConstraints: [
                                {
                                    field: VisitDatetimeField.EFFECTIVE_DATE,
                                    relativeTo: VisitDatetimeField.EFFECTIVE_DATE,
                                    gteOffsetMinutes: 0,
                                    lteOffsetMinutes: 0,
                                },
                            ],
                        },
                    },
                },
            },
        },
        ordering: { fullName: Ordering.ASC },
        rowToLink: ({ id }: { id: string }) => `/patients/${id}/scheduling`,
        emptyTableText: 'No patients missing Post Hospital Orders.',
    },
};

// Exclude those non-visit task services from Pinnacle's homepage "Today's
// Visits" list. FE-617 / PRD-546.
//
// Two layered exclusions, OR'd together: the explicit service-id list above,
// plus anything on the `Misc` discipline. The discipline arm is forward-looking
// — it catches future non-visit-task services Pinnacle files under `Misc`
// (today only Consent, Disaster Plan, Medication Profile) without a code change.
const TODAYS_VISITS_EXCLUDE_NON_VISIT_TASKS_FILTER: VisitFilter = {
    NOT: {
        services: { externalIds: { value: { inList: NON_VISIT_TASK_SERVICE_IDS } } },
        OR: {
            disciplines: { code: { exact: 'Misc' } },
        },
    },
};

// Surfaces visits assigned to a clinician who has no active shift on the
// visit's date, so schedulers can reassign them. Modeled on NorthStar's
// NORTH_STAR_VISITS_WITHOUT_ACTIVE_SHIFTS queue (FE-697 / ENG-7552). ENG-8714.
const VISITS_WITHOUT_SHIFTS_CONTAINER: VisitDashboardContainer = {
    key: PINNACLE_CONTAINER_KEYS.visitsWithoutActiveShifts,
    label: 'Visits Without Shifts',
    defaultEnabled: true,
    hoverText: 'Visits without active shifts',
    heavyQuery: true,
    snoozable: true,
    extraProps: {
        showClinicianColumn: true,
    },
    tableConfig: {
        entity: RootEntity.VISIT,
        highlightLinkedVisit: true,
        emptyTableText: 'No visits without shifts.',
        filters: {
            patient: { isActive: { exact: true } },
            hasActiveShiftOnDate: { exact: false },
            outcome: { exact: VisitOutcome.NONE },
            assignedClinicians: {
                employmentType: {
                    inList: [EmploymentType.FULL_TIME, EmploymentType.PART_TIME],
                },
            },
            // Rolling 7-day window starting today. The date window (effectiveDate)
            // is end-inclusive (today .. today+6); the datetime window
            // (latestEndDatetime) is end-exclusive on the date boundary
            // (today 00:00 .. today+7 00:00), covering the same 7 calendar days.
            AND: {
                effectiveDate: {
                    gte: GraphInputHelpers.date(moment()),
                    lte: GraphInputHelpers.date(moment().add(6, 'days')),
                },
                OR: {
                    effectiveDate: {
                        isNull: true,
                    },
                    latestEndDatetime: {
                        gte: GraphInputHelpers.datetime(moment().startOf('day')),
                        lt: GraphInputHelpers.datetime(moment().add(7, 'days').startOf('day')),
                    },
                },
            },
        },
        ordering: {
            effectiveSortDate: Ordering.ASC,
        },
        rowToLink: ({ id }: { id: string }) => `/visits/${id}`,
    },
};

const PINNACLE_VISITS_AWAITING_AUTHORIZATION_QUEUE: VisitDashboardContainer = {
    key: PINNACLE_CONTAINER_KEYS.visitsAwaitingAuthorization,
    label: 'Scheduled Visits Awaiting Authorization',
    defaultEnabled: true,
    hoverText:
        "Visits that can't be created in WellSky because there isn't an available authorization to cover them.",
    heavyQuery: true,
    snoozable: true,
    tableConfig: {
        entity: RootEntity.VISIT,
        highlightLinkedVisit: true,
        filters: {
            outcome: { exact: VisitOutcome.NONE },
            syncStatus: {
                state: { exact: SyncState.FAILING },
                lastFailure: {
                    userSafeErrorType: {
                        inList: AUTH_RELATED_SYNC_ERROR_TYPES,
                    },
                },
            },
        },
        ordering: { effectiveSortDate: Ordering.ASC },
        rowToLink: defaultVisitsRowToLink,
    },
};

export const PINNACLE_UAT_DASHBOARD_CONFIG: IDashboardVariationConfig = {
    todaysVisitsFilter: TODAYS_VISITS_EXCLUDE_NON_VISIT_TASKS_FILTER,
    containers: [
        /* #1  */ PINNACLE_INCOMPLETE_CARE_TEAM_CONTAINER,
        /* #2  */ SOC_CONTAINER,
        /* #3  */ ROC_CONTAINER,
        /* #4  */ EVAL_CONTAINER,
        // #5  (removed) OVERDUE_VISITS_CONTAINER,
        /* #6  */ POTENTIAL_RESCHEDULES_CONTAINER,
        /* #7  */ URGENT_VISITS_NEEDING_SCHEDULING_CONTAINER,
        /* #8  */ PATIENTS_NEEDING_SCHEDULING_CONTAINER,
        /* #9  */ OVERSCHEDULED_PATIENTS_CONTAINER,
        /* #10 */ UNDERSCHEDULED_PATIENTS_CONTAINER,
        /* #11 */ INACTIVE_CLINICIAN_VISITS_CONTAINER,
        /* #12 */ AGENCY_DISCHARGED_PATIENTS_WITH_PENDING_VISITS_CONTAINER,
        /* #13 */ DISCIPLINE_DISCHARGE_PATIENTS_WITH_PENDING_VISITS_CONTAINER,
        /* #14 */ DISCONTINUED_FREQUENCIES_WITH_PENDING_VISITS_CONTAINER,
        /* #15 */ TRANSFERRED_PATIENTS_WITH_PENDING_VISITS_CONTAINER,
        /* #16 */ PENDING_HHA_SUPERVISORY_CONTAINER,
        /* #17 */ PENDING_LPN_SUPERVISORY_CONTAINER,
        /* #18 */ PENDING_THERAPY_SUPERVISORY_CONTAINER,
        /* #19 */ PENDING_THERAPY_REEVALUATION_CONTAINER,
        /* #20 */ UPCOMING_END_OF_EPISODE_CONTAINER,
        /* #21 */ VISITS_SCHEDULED_BEFORE_SOC_CONTAINER,
        /* #22 */ CONFLICTING_DISCHARGE_AND_RECERT_VISITS_CONTAINER,
        /* #23 */ UPCOMING_DISCHARGE_WITH_FUTURE_PENDING_VISITS_CONTAINER,
        /* #24 */ COMPLETED_VISITS_MISSING_AN_ORDER_CONTAINER,
        /* #25 */ ACTIVE_PATIENTS_WITH_NO_VISITS_CONTAINER,
        /* #26 */ UNRESOLVED_EPISODES_CONTAINER,
        // #27 (TODO): Overlapping Episodes -- need BE filter support
        // #28 (TODO): Missing SOC Tasks -- Pending service code review/updates
        /* #29 */ PATIENTS_MISSING_CMS_485_CONTAINER,
        /* #30 */ PATIENTS_MISSING_NOMNC_CONTAINER,
        /* #31 */ PATIENTS_MISSING_SCIC_ORDER_CONTAINER,
        /* #32 */ PATIENTS_MISSING_POST_HOSPITAL_ORDER_CONTAINER,
        /* #33 */ VISITS_ASSIGNED_TO_CONTRACT_AGENCY_CONTAINER,
        /* #34 */ ALL_TRANSFERRED_PATIENTS_CONTAINER,
        /* #35 */ MISSED_VISITS_CONTAINER,
        /* #36 */ VISITS_WITHOUT_SHIFTS_CONTAINER,
        /* #37 */ PINNACLE_VISITS_AWAITING_AUTHORIZATION_QUEUE,
    ],
};

export const PINNACLE_DASHBOARD_CONFIG: IDashboardVariationConfig = {
    todaysVisitsFilter: TODAYS_VISITS_EXCLUDE_NON_VISIT_TASKS_FILTER,
    containers: [
        /* #1  */ PINNACLE_INCOMPLETE_CARE_TEAM_CONTAINER,
        /* #2  */ SOC_CONTAINER,
        /* #3  */ ROC_CONTAINER,
        /* #4  */ EVAL_CONTAINER,
        // #5  (removed) OVERDUE_VISITS_CONTAINER,
        // #6 (TODO - removed for rollout): POTENTIAL_RESCHEDULES_CONTAINER,
        /* #7  */ URGENT_VISITS_NEEDING_SCHEDULING_CONTAINER,
        /* #8  */ PATIENTS_NEEDING_SCHEDULING_CONTAINER,
        /* #9  */ OVERSCHEDULED_PATIENTS_CONTAINER,
        /* #10 */ UNDERSCHEDULED_PATIENTS_CONTAINER,
        /* #11 */ INACTIVE_CLINICIAN_VISITS_CONTAINER,
        /* #12 */ AGENCY_DISCHARGED_PATIENTS_WITH_PENDING_VISITS_CONTAINER,
        /* #13 */ DISCIPLINE_DISCHARGE_PATIENTS_WITH_PENDING_VISITS_CONTAINER,
        /* #14 */ DISCONTINUED_FREQUENCIES_WITH_PENDING_VISITS_CONTAINER,
        /* #15 */ TRANSFERRED_PATIENTS_WITH_PENDING_VISITS_CONTAINER,
        /* #16 */ PENDING_HHA_SUPERVISORY_CONTAINER,
        /* #17 */ PENDING_LPN_SUPERVISORY_CONTAINER,
        /* #18 */ PENDING_THERAPY_SUPERVISORY_CONTAINER,
        /* #19 */ PENDING_THERAPY_REEVALUATION_CONTAINER,
        /* #20 */ UPCOMING_END_OF_EPISODE_CONTAINER,
        /* #21 */ VISITS_SCHEDULED_BEFORE_SOC_CONTAINER,
        /* #22 */ CONFLICTING_DISCHARGE_AND_RECERT_VISITS_CONTAINER,
        /* #23 */ UPCOMING_DISCHARGE_WITH_FUTURE_PENDING_VISITS_CONTAINER,
        /* #24 */ COMPLETED_VISITS_MISSING_AN_ORDER_CONTAINER,
        /* #25 */ ACTIVE_PATIENTS_WITH_NO_VISITS_CONTAINER,
        // #26 (TODO - removed for rollout or maybe forever): UNRESOLVED_EPISODES_CONTAINER,
        // #27 (TODO): Overlapping Episodes -- need BE filter support
        // #28 (TODO): Missing SOC Tasks -- Pending service code review/updates
        /* #29 */ PATIENTS_MISSING_CMS_485_CONTAINER,
        /* #30 */ PATIENTS_MISSING_NOMNC_CONTAINER,
        /* #31 */ PATIENTS_MISSING_SCIC_ORDER_CONTAINER,
        /* #32 */ PATIENTS_MISSING_POST_HOSPITAL_ORDER_CONTAINER,
        /* #33 */ VISITS_ASSIGNED_TO_CONTRACT_AGENCY_CONTAINER,
        /* #34 */ ALL_TRANSFERRED_PATIENTS_CONTAINER,
        /* #35 */ MISSED_VISITS_CONTAINER,
        /* #36 */ VISITS_WITHOUT_SHIFTS_CONTAINER,
        /* #37 */ PINNACLE_VISITS_AWAITING_AUTHORIZATION_QUEUE,
    ],
};
